import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useMediaAccess } from '../hooks/useMediaAccess';

import type { ManuscriptContract } from '../types';

/** `unavailable` means the contract can't be signed at all (no revision) — never resolves to `ready`. */
export type MediaAccessStatus = 'unavailable' | 'loading' | 'ready' | 'error';

interface DocumentContextValue {
  contract: ManuscriptContract | null;
  isLoading: boolean;
  error: string | null;
  highlightedBlockId: string | null;

  /** Signed query string for the current contract's figures, or `null` until `mediaAccessStatus` is `'ready'`. */
  mediaAccessQueryString: string | null;
  mediaAccessStatus: MediaAccessStatus;

  setContract: (contract: ManuscriptContract | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  highlightBlock: (blockId: string | null) => void;
}

const DocumentContext = createContext<DocumentContextValue | null>(null);

interface DocumentProviderProps {
  children: ReactNode;
}

export function DocumentProvider({ children }: DocumentProviderProps) {
  const [contract, setContract] = useState<ManuscriptContract | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedBlockId, setHighlightedBlockId] = useState<string | null>(null);

  const highlightBlock = useCallback((blockId: string | null) => {
    setHighlightedBlockId(blockId);
  }, []);

  const {
    mediaAccess,
    error: mediaAccessError,
    isLoading: mediaAccessLoading,
  } = useMediaAccess(contract?.submissionId, contract?.version, contract?.revision);

  const mediaAccessStatus: MediaAccessStatus =
    !contract?.submissionId || !contract?.version || contract?.revision === undefined
      ? 'unavailable'
      : mediaAccessError
        ? 'error'
        : mediaAccess
          ? 'ready'
          : mediaAccessLoading
            ? 'loading'
            : 'error';

  const mediaAccessQueryString = mediaAccess?.queryString ?? null;

  // Consumers (DocumentViewer chief among them) re-render on any identity
  // change here, so this must only change when a field actually did --
  // otherwise every unrelated state update (e.g. a media-access status flip)
  // would re-render the whole document tree.
  const value: DocumentContextValue = useMemo(
    () => ({
      contract,
      isLoading,
      error,
      highlightedBlockId,
      mediaAccessQueryString,
      mediaAccessStatus,
      setContract,
      setLoading,
      setError,
      highlightBlock,
    }),
    [
      contract,
      isLoading,
      error,
      highlightedBlockId,
      mediaAccessQueryString,
      mediaAccessStatus,
      highlightBlock,
    ]
  );

  return <DocumentContext.Provider value={value}>{children}</DocumentContext.Provider>;
}

export function useDocument(): DocumentContextValue {
  const context = useContext(DocumentContext);

  if (!context) {
    throw new Error('useDocument must be used within DocumentProvider');
  }
  return context;
}
